import React from "react";
import { ArrowRightOutlined } from "@ant-design/icons";
import { Tokens } from "Shared/Tokens";
import { getFormattedAmount, formatSmartPrice } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { formatShortAddress } from "utils/Wallets";

const WalletPortfolioCard = (props) => {
    const {
        wallet = {},
        balanceData = null,
        currency = "USDT",
        onClick,
    } = props;

    const chain =
        String(wallet.chain || "solana")
            .trim()
            .toLowerCase() === "ethereum"
            ? "ethereum"
            : "solana";
    const isEth = chain === "ethereum";
    const chainBadge = isEth ? "ETH" : "SOL";
    const chainLabel = isEth ? "Ethereum" : "Solana";
    const shortAddr = formatShortAddress(wallet.address);

    const hasData = Boolean(
        balanceData && typeof balanceData.totalValueUsd === "number"
    );
    const totalValue = Number(balanceData?.totalValueUsd) || 0;
    const nativeBalance = Number(balanceData?.balance) || 0;
    const tokenList = Array.isArray(balanceData?.tokens)
        ? balanceData.tokens
        : [];
    const assetCount = (nativeBalance > 0 ? 1 : 0) + tokenList.length;
    const currSymbol = getCurrencySymbol(
        balanceData?.currency || currency || "USDT"
    );

    return (
        <div
            data-testid={`wallet-portfolio-card-${wallet.uid || chain}`}
            role="button"
            tabIndex={0}
            onClick={() => onClick && onClick(wallet)}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (onClick) onClick(wallet);
                }
            }}
            style={{
                padding: "20px",
                borderRadius: Tokens.radiusCard,
                border: `1px solid ${Tokens.hairline}`,
                backgroundColor: Tokens.card,
                marginBottom: "16px",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <div
                        style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: Tokens.radiusControl,
                            backgroundColor: Tokens.raised,
                            border: `1px solid ${Tokens.hairlineStrong}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: Tokens.textPrimary,
                            fontFamily: Tokens.fontMono,
                            fontWeight: 500,
                            fontSize: "12px",
                            flexShrink: 0,
                        }}
                    >
                        {chainBadge}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                flexWrap: "wrap",
                            }}
                        >
                            <span
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    lineHeight: 1.25,
                                }}
                            >
                                {wallet.name || `${chainLabel} wallet`}
                            </span>
                            <span
                                data-testid={`wallet-card-address-${
                                    wallet.uid || chain
                                }`}
                                style={{
                                    color: Tokens.textMuted,
                                    fontSize: "12px",
                                    fontFamily: Tokens.fontMono,
                                    backgroundColor: Tokens.raised,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusTag,
                                    padding: "2px 6px",
                                }}
                            >
                                {shortAddr}
                            </span>
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "13px",
                                marginTop: "4px",
                            }}
                        >
                            {chainLabel} on-chain wallet
                        </div>
                    </div>
                </div>
                <ArrowRightOutlined
                    style={{
                        color: Tokens.textSecondary,
                        fontSize: "14px",
                        flexShrink: 0,
                    }}
                />
            </div>

            {hasData && (
                <div
                    data-testid={`wallet-card-summary-${wallet.uid || chain}`}
                    style={{
                        marginTop: "16px",
                        paddingTop: "12px",
                        borderTop: `1px solid ${Tokens.hairline}`,
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: "12px",
                        alignItems: "start",
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "12px",
                                marginBottom: "4px",
                            }}
                        >
                            Balance
                        </div>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "14px",
                                fontWeight: 500,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {currSymbol}
                            {getFormattedAmount(totalValue, true, 2)}
                        </div>
                    </div>

                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "12px",
                                marginBottom: "4px",
                            }}
                        >
                            Native {chainBadge}
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "14px",
                                fontWeight: 400,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {formatSmartPrice(nativeBalance)} {chainBadge}
                        </div>
                    </div>

                    <div style={{ minWidth: 0, textAlign: "right" }}>
                        <div
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "12px",
                                marginBottom: "4px",
                            }}
                        >
                            Holdings
                        </div>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "14px",
                                fontWeight: 500,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {assetCount} {assetCount === 1 ? "asset" : "assets"}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WalletPortfolioCard;
